import TagManager from 'react-gtm-module';
import { TrackerEnum } from 'enums/TrackerType';

// Suppress uncaught GTM errors that occur asynchronously
const originalOnError = window.onerror;
window.onerror = (message, source, lineno, colno, error): boolean => {
    // Check if the error is from GTM (including anonymous/eval'd code from GTM)
    const isGtmError =
        source?.includes('googletagmanager.com') ||
        source?.includes('gtm.js') ||
        (source === '' && message?.toString().includes('length')) || // Anonymous GTM eval errors
        error?.stack?.includes('googletagmanager.com');

    if (isGtmError) {
        console.warn('GTM error suppressed:', message);
        return true; // Prevents the error from propagating
    }
    // Call the original handler if it exists
    if (originalOnError) {
        return originalOnError(message, source, lineno, colno, error) as boolean;
    }
    return false;
};

// Also catch unhandled promise rejections from GTM
window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent): void => {
    const reason = event.reason?.toString() || '';
    if (reason.includes('googletagmanager') || reason.includes('gtm') || reason.includes('length')) {
        console.warn('GTM promise rejection suppressed:', reason);
        event.preventDefault();
    }
});

// Patch Array.prototype to prevent GTM from crashing on undefined arrays
const originalArrayLength = Object.getOwnPropertyDescriptor(Array.prototype, 'length');
if (originalArrayLength) {
    // This ensures GTM doesn't crash when accessing length on malformed data
    window.addEventListener(
        'error',
        (event: ErrorEvent): void => {
            if (event.message?.includes("Cannot read properties of undefined (reading 'length')")) {
                const stack = event.error?.stack || '';
                if (stack.includes('googletagmanager') || stack.includes('gtm.js') || event.filename?.includes('gtm')) {
                    console.warn('GTM length error suppressed');
                    event.preventDefault();
                    event.stopPropagation();
                }
            }
        },
        true
    );
}

export class AnalyticsService {
    private static instance: AnalyticsService;
    private isGtmInitialized = false;

    // Safe wrapper for dataLayer calls
    private safeDataLayerPush = (data: { dataLayer: Record<string, unknown> }): void => {
        if (this.isGtmInitialized) {
            try {
                TagManager.dataLayer(data);
            } catch (error) {
                console.warn('GTM dataLayer push failed:', error);
            }
        }
    };

    constructor() {
        this.initializeGTM();
        document.addEventListener(TrackerEnum.PAGE_VIEW, this.sendPageChangeAnalytics);
        document.addEventListener(TrackerEnum.PAYMENT_GUARD_CLAIMPROCESS, (ev: Event) => {
            if (ev instanceof CustomEvent) {
                const { claimType, distributorName, id } = ev.detail;
                if (id) {
                    switch (id) {
                        case TrackerEnum.CLAIMSTEP_START:
                        case TrackerEnum.CLAIMSTEP_PERSONAL_INFO:
                        case TrackerEnum.CLAIMSTEP_DISABILITY_DETAILS:
                        case TrackerEnum.CLAIMSTEP_JOBLOSS_DETAILS:
                        case TrackerEnum.CLAIMSTEP_EMPLOYER_INFO:
                        case TrackerEnum.CLAIMSTEP_HEALTHCARE_INFO:
                        case TrackerEnum.CLAIMSTEP_COMMENTS:
                        case TrackerEnum.CLAIMSTEP_REVIEW:
                        case TrackerEnum.CLAIMSTEP_SUBMITTED:
                        case TrackerEnum.CLAIMSTEP_SUBMIT_ATTEMPT_FAILED:
                            this.claimProcessAnalytics.sendToDataLayer(claimType, distributorName, id);
                            break;
                    }
                }
            }
        });
        document.addEventListener(TrackerEnum.BORROWER_NAME_CHANGED, (ev: Event) => {
            if (ev instanceof CustomEvent) {
                const { claimType, distributorName, isBorrowerFirstLastNamePopulated } = ev.detail;
                this.borrowerNameFieldAnalytics.sendToDataLayer(
                    claimType,
                    distributorName,
                    isBorrowerFirstLastNamePopulated
                );
            }
        });
        document.addEventListener(TrackerEnum.LAST_CLAIM_FIELD_CHANGED, (ev: Event) => {
            if (ev instanceof CustomEvent) {
                const { claimType, distributorName, lastClaimFieldChanged } = ev.detail;
                this.lastClaimFieldChangedAnalytics.sendToDataLayer(claimType, distributorName, lastClaimFieldChanged);
            }
        });
        document.addEventListener(TrackerEnum.DISTRIBUTOR_SELECT, this.sendDIDSelectionPageAnalytics);
    }
    private sendPageChangeAnalytics = (): void => {
        const currentPath = window.location.pathname;
        const event = TrackerEnum.PAGE_VIEW;
        const page = currentPath.split('/');
        this.safeDataLayerPush({
            dataLayer: {
                event,
                page_location: currentPath,
                page_title: page
            }
        });
    };
    private sendDIDSelectionPageAnalytics = (): void => {
        const currentPath = window.location.pathname;
        const event = TrackerEnum.PAGE_VIEW;
        const page = 'DID Selection';
        this.safeDataLayerPush({
            dataLayer: {
                event,
                page_location: currentPath,
                page_title: page
            }
        });
    };
    private claimProcessAnalytics = {
        sendToDataLayer: (claimType: string, distributorName: string, processState: string): void => {
            this.safeDataLayerPush({
                dataLayer: {
                    event: TrackerEnum.PAYMENT_GUARD_CLAIMPROCESS,
                    claimType: claimType,
                    distributorName: distributorName,
                    claimStep: processState
                }
            });
        }
    };
    private borrowerNameFieldAnalytics = {
        sendToDataLayer: (
            claimType: string,
            distributorName: string,
            isBorrowerFirstLastNamePopulated: boolean
        ): void => {
            this.safeDataLayerPush({
                dataLayer: {
                    event: TrackerEnum.BORROWER_NAME_CHANGED,
                    claimType: claimType,
                    distributorName: distributorName,
                    isBorrowerFirstLastNamePopulated: isBorrowerFirstLastNamePopulated
                }
            });
        }
    };
    private lastClaimFieldChangedAnalytics = {
        sendToDataLayer: (claimType: string, distributorName: string, lastClaimFieldChanged: string): void => {
            this.safeDataLayerPush({
                dataLayer: {
                    event: TrackerEnum.LAST_CLAIM_FIELD_CHANGED,
                    claimType: claimType,
                    distributorName: distributorName,
                    lastClaimFieldChanged: lastClaimFieldChanged
                }
            });
        }
    };
    private initializeGTM = (): void => {
        const gtmId = process.env.REACT_APP_PAYGUARD_APP_GTMID;
        const userEnv = process.env.REACT_APP_USER_ENV;

        // Skip GTM initialization in development to avoid GTM container errors
        // Remove this condition when GTM container issues are resolved
        if (userEnv === 'develop' || userEnv === 'local') {
            console.info('GTM skipped in development environment');
            return;
        }

        // Only initialize GTM if we have a valid GTM ID (not undefined or a placeholder token)
        if (gtmId && !gtmId.startsWith('__')) {
            try {
                const tagManagerArgs = {
                    gtmId
                };
                TagManager.initialize(tagManagerArgs);
                this.isGtmInitialized = true;
            } catch (error) {
                console.warn('GTM initialization failed:', error);
            }
        } else {
            console.warn('GTM not initialized: Invalid or missing REACT_APP_PAYGUARD_APP_GTMID');
        }
    };
    public static init(): AnalyticsService {
        if (!AnalyticsService.instance) {
            AnalyticsService.instance = new AnalyticsService();
        }
        return AnalyticsService.instance;
    }
}
